import { prepareToast } from './toast';
import { ErrorTier, type NormalizedError } from '@/errors';
import { useAppStore } from '@/stores/app';
import { useErrorStore } from '@/stores/error';

// Re-exported, so the ~100 existing importers of this module are unaffected by the split. What
// lives next door is everything that needs no store: the phase type and the two rules over it.
export { isBusyPhase, resolveContentPhase } from './loadingPhase';
export type { LoadingPhase } from './loadingPhase';

import type { LoadingPhase } from './loadingPhase';

/**
 * Async state for one operation: loading / finished / failed, plus the error that caused it.
 *
 * This class used to do five jobs — async state, toast presentation, Sentry reporting, HTTP semantics
 * and 401 navigation — from a per-request object instantiated ~70 times. There was nowhere to put
 * policy like "rate-limit error toasts" or "don't report expected errors", because policy is a
 * single-instance concern. Those four jobs now live in `src/errors/` and `src/stores/error.ts`.
 *
 * The public surface is unchanged on purpose, so all ~97 existing `setError` call sites and both the
 * `ref(...)` and `reactive(...)` wrappings keep working untouched.
 *
 * Which to use:
 *  - `setError(e)`   — "this async operation failed, use the default presentation".
 *  - `errorStore.handle(e, { ... })` — anything else: a different tier, a retry, a fatal screen.
 */
export class LoadingStateModel {
  isLoading: boolean = false;
  isFinished: boolean = false;
  isFailed: boolean = false;
  message: string = '';
  options: object = {};

  /** Set by `setError`. Read it to render `<ErrorState>` instead of relying on a toast. */
  error?: NormalizedError;

  /**
   * The five states a view actually has to render, in one place.
   *
   * The flags below already carried this: `isFinished` latches on the first `stopLoadingAndFinish()`
   * and survives `startLoading()`, so `isLoading && isFinished` is a reload with usable values still
   * on screen. Nothing read it — all 48 `ProgressSpinner` sites keyed on `isLoading` alone and blanked
   * their content on every refresh. Switch on `phase` rather than re-deriving the rule at the call site.
   *
   * `failed` is checked before `ready` because a failed request sets both: the `finally` that runs
   * `stopLoadingAndFinish()` does not know the `catch` fired.
   */
  get phase(): LoadingPhase {
    if (this.isLoading) {
      return this.isFinished ? 'reloading' : 'first-load';
    }
    if (this.isFailed) {
      return 'failed';
    }
    return this.isFinished ? 'ready' : 'empty';
  }

  constructor({
    isLoading = false, isFinished = false, isFailed = false, msg = '', options = {}
  }) {
    this.isLoading = isLoading;
    this.isFinished = isFinished;
    this.isFailed = isFailed;
    this.message = msg;
    this.options = options;

    if (Object.keys(options).length > 0) {
      this.setOptions(options);
    }
  }
  setState({
    isLoading = false, isFinished = false, isFailed = false, msg = '', options = {}
  }) {
    this.isLoading = isLoading;
    this.isFinished = isFinished;
    this.isFailed = isFailed;
    this.message = msg;
    // Guarded on the parameter, not `this.options`. The old check read the instance field while the
    // constructor read the argument, so `setState({ options })` silently dropped them on every loader
    // that was constructed without options — which is all of them.
    if (Object.keys(options || {}).length > 0) {
      this.setOptions(options);
    }
  }
  setOptions(options: object) {
    this.options = { ...this.options, ...options };
  }

  /**
   * Record a failure and hand it to the error store, which classifies, reports and presents it.
   *
   * Returns the normalized error so a caller that wants an inline error state can use it directly.
   */
  setError(error: unknown): NormalizedError {
    this.isFailed = true;
    // The source is deliberately not forced to HTTP. A request that failed already carries the
    // interceptor's classification on the error itself, so this hint was ignored for it — and the
    // only errors it *did* reach are the non-HTTP ones (a GeolocationPositionError, the fabricated
    // `Locked` literals, a bare string), which it then mislabelled as http in every report.
    const normalized = useErrorStore().handle(error);
    this.error = normalized;
    this.message = normalized.serverMessage ?? this.message;
    return normalized;
  }

  /**
   * Fail the whole view rather than a corner of it: replaces the router outlet with an error screen.
   * For an initial load the user cannot work around — a failed study fetch, say.
   */
  setFatalError(error: unknown, retry?: () => void | Promise<void>): NormalizedError {
    this.isFailed = true;
    const normalized = useErrorStore().handle(error, {
      tier: ErrorTier.FATAL,
      retry
    });
    this.error = normalized;
    return normalized;
  }

  setSuccess(message: string, title: string = 'Success!') {
    useAppStore().addToast(prepareToast('success', title, message));
  }
  startLoading() {
    this.isLoading = true;
    // Clear the previous failure, or an inline error state latches forever: `isFailed` was never
    // reset, which is why the only two inline error states in the app never recovered on retry.
    this.isFailed = false;
    this.error = undefined;
  }
  stopLoadingAndFinish() {
    this.isLoading = false;
    this.isFinished = true;
  }

  /** Back to a clean slate, for a panel that unmounts and remounts. */
  reset() {
    this.isLoading = false;
    this.isFinished = false;
    this.isFailed = false;
    this.message = '';
    this.error = undefined;
  }
}
